import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatestWith, defer, from, takeUntil } from 'rxjs';
import { ComponentWithDestroy } from 'src/app/modules/shared/component-with-destroy';
import { AuthService } from 'src/app/services/auth.service';
import { HeaderService } from 'src/app/services/header.service';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';
import { BotModel } from 'src/app/models/bot';
import { BotsService, CorpsService, HierarchyElementsService, UsersService } from 'src/app/services/firestore';
import { DumbledoreService } from 'src/app/services/dumbledore.service';

@Component({
  selector: 'app-bot-analytics',
  templateUrl: './bot-analytics.component.html',
  styleUrls: ['./bot-analytics.component.scss'],
})
export class BotAnalyticsComponent extends ComponentWithDestroy implements OnInit {
  botExists = false;
  loading = false;
  bot: BotModel;
  botId: string;
  currentTab: string;
  ignoreCronos: boolean = false;

  endDate: Date = new Date();
  startDate: Date = new Date();
  newEndDate: Date = new Date();
  newStartDate: Date = new Date();

  private isAdminLogged = false;
  public tabs = [
    { label: 'Overview', slug: 'overview', visible: true },
    { label: 'Reports', slug: 'new-reports', visible: true },
  ];
  public currentUserEmail: string | null;
  public currentUserName: string | null;

  @Input() reportType: string;
  @Output() reportTypeEmitter = new EventEmitter<string>();

  public showReport = false;

  constructor(
    private headerService: HeaderService,
    private route: ActivatedRoute,
    private corpsService: CorpsService,
    private botsService: BotsService,
    private hierarchyElementsService: HierarchyElementsService,
    private authService: AuthService,
    private clientEnvironmentService: ClientEnvironmentService,
    private userService: UsersService,
    private dumbledoreService: DumbledoreService,
  ) {
    super();
    this.loading = false;
    this.currentTab = 'overview';
  }

  public trackTab(_index: number, tab: { slug: string }): string {
    return tab.slug;
  }

  async ngOnInit() {
    const json = this.getSessionParameters();
    if (json != null) {
      this.startDate = new Date(json.startDate);
      this.newStartDate = this.startDate;
      this.endDate = new Date(json.endDate);
      this.reportType = json.reportType;
      this.reportTypeEmitter.emit(this.reportType);
    } else {
      this.startDate.setMonth(this.endDate.getMonth() - 6);
      this.newStartDate = this.startDate;
      this.reportType = 'E';
    }

    const reportDays = (this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24);
    this.showReport = reportDays <= 186;

    this.loading = true;
    this.route
      .parent!.paramMap.pipe(combineLatestWith(defer(() => from(this.authService.currentUser))), takeUntil(this.destroy$))
      .subscribe(async ([params, user]) => {
        const corpId = params.get('corp');
        const hierarchyElementSystemName = params.get('hierarchyElementSystemName');
        const botCode = params.get('bot');
        if (!corpId || !hierarchyElementSystemName || !botCode || !user) {
          return;
        }

        this.userService
          .getUserById(user.uid)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: userModel => {
              this.currentUserEmail = user.email;
              this.currentUserName = `${userModel?.firstName} ${userModel?.lastName}`;
              const isAdminLogged = userModel?.role?.toUpperCase() === 'ADMIN';
              if (isAdminLogged !== this.isAdminLogged) {
                this.isAdminLogged = isAdminLogged;
                const standardTabs = this.tabs.filter(tab => tab.slug !== 'recall-campaigns');
                this.tabs = isAdminLogged ? [...standardTabs, { label: 'Create Campaign', slug: 'recall-campaigns', visible: true }] : standardTabs;
              }
            },
            error: () => {
              throw new Error('Error loading user...');
            },
          });

        this.corpsService
          .getCorpById(corpId)
          .pipe(
            combineLatestWith(
              this.hierarchyElementsService.getHierarchyElement(`${corpId}-${hierarchyElementSystemName}`),
              this.clientEnvironmentService.items$,
            ),
            takeUntil(this.destroy$),
          )
          .subscribe(async ([corp, hierarchyElement, envs]) => {
            if (!corp || !hierarchyElement || !envs) {
              this.loading = false;
              return;
            }

            try {
              const bot = await this.botsService.getBotBy(corpId, hierarchyElementSystemName, botCode, envs);
              if (bot) {
                this.bot = bot;
                this.botId = bot.id;
                this.botExists = true;
              }
            } catch {
              throw new Error('Valid bot config required.');
            }

            try {
              const botConfig = await this.dumbledoreService.getBotConfigurationByDealershipCode(`${corp.id}-${hierarchyElementSystemName}`);
              this.ignoreCronos = botConfig?.ignoreCronos ?? false;
            } catch {
              console.error(`Error fetching bot configuration from Dumbledore for dealer code: ${corp.id}-${hierarchyElementSystemName}`);
            }

            this.refreshUI();
            this.loading = false;
          });
      });
  }

  private getSessionParameters() {
    try {
      const storage = window.localStorage;
      if (storage) {
        const value = storage.getItem('dashboardParameters') || null;
        localStorage.removeItem('dashboardParameters');
        if (value != null) {
          const json = JSON.parse(value);
          return json;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  refreshUI() {
    this.headerService.setPageTitle(`${this.bot.label} Analytics`);
  }

  getReportType($event) {
    this.reportType = $event;
    this.reportTypeEmitter.emit(this.reportType);
  }

  saveSessionParameters() {
    const reportDays = (this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24);
    this.showReport = reportDays <= 186;

    try {
      const storage = window.localStorage;
      if (storage) {
        const value = {
          startDate: this.startDate,
          endDate: this.endDate,
          reportType: '',
          conversationId: '',
          filters: [],
          sortby: '',
        };
        storage.setItem('dashboardParameters', JSON.stringify(value));
      }
    } catch (err) {
      console.error('Failed storing messages', err);
    }
  }

  setTab(tab: string) {
    this.saveSessionParameters();
    this.currentTab = tab;
  }
}
